/**
 * @file draft-po-editor.tsx
 * @module app/modules/uProcure/po-management/draft-po-editor
 * @description Edit mode of a draft PO: change quantity, qty type and cost price, remove
 *   products, add products of the supplier, choose the supplier, save; and the Submit dialog
 *   (delivery date + transport) that turns the draft into a Pending PO.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import APICallService from "../../../../api/apiCallService";
import { PURCHASEORDER, vendor } from "../../../../api/apiEndPoints";
import { POManagement, SelfTransport, UTradeTransport, WarehouseManger } from "../../../../utils/constants";
import { CustomSelect } from "../../../custom/Select/CustomSelect";
import { error, success } from "../../../../Global/toast";
import { DraftLine, DraftPoDetail, Option, SupplierProduct, SupplierRecord } from "./draft-po.types";
import { draftGroupLabel } from "./draft-po-group";
import DraftCreditNote from "./draft-po-credit";

const QTY_TYPES = [
  { value: 1, label: "Carton with Dozens" },
  { value: 2, label: "Dozen with Pieces" },
  { value: 3, label: "Piece" },
  { value: 4, label: "Carton with Pieces" },
];

export interface EditLine {
  variant: string;
  title: string;
  refKey?: string;
  quantityType: number;
  quantity: number | "";
  costPrice: number | "" | null;
  /** current Price Catalog cost price (null = not set in the catalog) */
  catalogCostPrice?: number | null;
  isNew?: boolean;
}

const callPatch = async (id: string, action: string | null, params: Record<string, unknown>, warehouse?: string) => {
  const api = new APICallService(
    PURCHASEORDER.updateDraftPurchaseOrder,
    params,
    action ? { id, action } : { id },
    "",
    "",
    "",
    POManagement,
    warehouse
  );
  return api.callAPI();
};

export const DraftPoEditor = ({
  draft,
  warehouse,
  onSaved,
  onCancel,
}: {
  draft: DraftPoDetail;
  warehouse?: string;
  onSaved: (updated: DraftPoDetail) => void;
  onCancel: () => void;
}) => {
  const [lines, setLines] = useState<EditLine[]>(
    draft.lines.map((l: DraftLine) => ({
      variant: String(l.variant),
      title: l.title,
      refKey: l.refKey,
      quantityType: l.quantityType,
      quantity: l.quantity,
      costPrice: l.costPrice ?? null,
      catalogCostPrice: l.catalogCostPrice ?? null,
    }))
  );
  const [supplier, setSupplier] = useState<{ value: string; label: string } | null>(
    draft.business ? { value: String(draft.business.reference), label: draft.business.name } : null
  );
  const [suppliers, setSuppliers] = useState<{ value: string; label: string }[]>([]);
  const [catalog, setCatalog] = useState<SupplierProduct[]>([]);
  const [saving, setSaving] = useState(false);

  // suppliers, only needed when the draft has none
  useEffect(() => {
    if (draft.business) return;
    (async () => {
      const api = new APICallService(vendor.listVendor, { pageNo: 1, limit: 500, sortKey: "ownerName", sortOrder: 1 }, "", "", "", "", WarehouseManger, warehouse);
      const res = await api.callAPI();
      setSuppliers((res?.records ?? []).map((b: SupplierRecord) => ({ value: b._id, label: b.ownerName || b.name || "" })));
    })();
  }, []);

  // products of the supplier, as on Add Purchase Order
  useEffect(() => {
    if (!supplier?.value) return;
    (async () => {
      const api = new APICallService(PURCHASEORDER.productListForPurchaseOrder, { "business[]": supplier.value }, "", "", "", "", WarehouseManger, "");
      const res = await api.callAPI();
      setCatalog(res?.records ?? []);
    })();
  }, [supplier?.value]);

  const set = (i: number, patch: Partial<EditLine>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const addOptions = catalog
    .filter((p) => !lines.some((l) => l.variant === String(p._id)))
    .map((p) => ({ value: String(p._id), label: p.title, record: p }));

  const save = async () => {
    const bad = lines.find((l) => !(Number(l.quantity) > 0) || !Number.isInteger(Number(l.quantity)));
    if (bad) {
      error(`Enter a whole quantity above 0 for ${bad.title}`);
      return;
    }
    setSaving(true);
    const res = await callPatch(
      draft._id,
      null,
      {
        ...(draft.business ? {} : { business: supplier?.value ?? "" }),
        lines: lines.map((l) => ({
          variant: l.variant,
          quantityType: l.quantityType,
          quantity: Number(l.quantity),
        })),
      },
      warehouse
    );
    setSaving(false);
    if (res && res.refKey) {
      success("Draft saved");
      onSaved(res);
    }
  };

  return (
    <div>
      {!draft.business && (
        <div className="mb-5" style={{ maxWidth: 420 }}>
          <label className="fs-14 fw-600 mb-2 required">Supplier</label>
          <CustomSelect placeholder="Choose supplier" backgroundColor="#fff" value={supplier} options={suppliers} onChange={setSupplier} />
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-row-bordered align-middle gs-4 gy-2 mb-0 fs-14">
          <thead>
            <tr className="fw-bold text-dark bg-light">
              <th>Product</th>
              <th style={{ width: 210 }}>Qty Type</th>
              <th style={{ width: 130 }}>Quantity</th>
              <th style={{ width: 170 }} title="Change cost prices in the Price Catalog">Cost Price (TSh)</th>
              <th className="text-end">Line Value</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.variant}>
                <td style={{ minWidth: 240 }}>
                  <div className="fw-600">
                    {l.title} {l.isNew && <span className="badge badge-light-success ms-1">new</span>}
                  </div>
                  {l.refKey && <div className="fs-12 text-gray-600">{l.refKey}</div>}
                </td>
                <td>
                  <Form.Select size="sm" value={l.quantityType} onChange={(e) => set(i, { quantityType: Number(e.target.value) })}>
                    {QTY_TYPES.map((q) => (
                      <option key={q.value} value={q.value}>
                        {q.label}
                      </option>
                    ))}
                  </Form.Select>
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    type="number"
                    min={1}
                    step={1}
                    value={l.quantity}
                    onChange={(e) => set(i, { quantity: e.target.value === "" ? "" : Number(e.target.value) })}
                  />
                </td>
                <td>
                  {typeof l.costPrice === "number" && l.costPrice > 0 ? (
                    <span className="fw-600">{l.costPrice.toLocaleString("en-US")}</span>
                  ) : (
                    <span className="text-danger fs-13">Not in Price Catalog</span>
                  )}
                  <div className="fs-12 text-gray-600">Price Catalog (read only)</div>
                </td>
                <td className="text-end">
                  {Number(l.costPrice) > 0 && Number(l.quantity) > 0
                    ? (Number(l.costPrice) * Number(l.quantity)).toLocaleString("en-US")
                    : "-"}
                </td>
                <td className="text-end">
                  <Button size="sm" variant="light-danger" onClick={() => setLines((p) => p.filter((_, idx) => idx !== i))}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-4 mt-5">
        <div style={{ minWidth: 380 }}>
          <CustomSelect
            placeholder={supplier ? "Add a product of this supplier" : "Choose the supplier first"}
            backgroundColor="#fff"
            value={null}
            disabled={!supplier}
            options={addOptions}
            onChange={(o: (Option & { record: SupplierProduct }) | null) => {
              if (!o) return;
              const p = o.record;
              const firstType = Number((p.quantityTypes ?? [])[0]?.type) || 4;
              setLines((prev) => [
                ...prev,
                { variant: String(p._id), title: p.title, refKey: p.refKey, quantityType: firstType, quantity: "", costPrice: null, isNew: true },
              ]);
            }}
          />
        </div>
        <div className="d-flex gap-2">
          <Button className="uw-btn uw-btn-secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button className="uw-btn uw-btn-primary" onClick={save} disabled={saving || (!draft.business && !supplier)}>
            {saving ? <Spinner animation="border" size="sm" /> : "Save Draft"}
          </Button>
        </div>
      </div>
    </div>
  );
};

/** Delivery date + transport, then creates the Pending PO from the draft. */
export const SubmitDraftPoModal = ({
  draft,
  warehouse,
  show,
  onHide,
  onSubmitted,
}: {
  draft: DraftPoDetail;
  warehouse?: string;
  show: boolean;
  onHide: () => void;
  onSubmitted: (po: { refKey: string }) => void;
}) => {
  const [date, setDate] = useState("");
  const [confirmBypass, setConfirmBypass] = useState(false);
  const below = draft.rules && !draft.rules.ok ? draft.rules : null;
  const oldPending = below?.oldPendingPos ?? [];
  // old Pending POs: one entry per PO with how many draft products it holds
  const oldPos = [...new Map(oldPending.map((p) => [p.refKey, p])).values()].map((p) => ({
    refKey: p.refKey,
    ageDays: p.ageDays,
    products: oldPending.filter((x) => x.refKey === p.refKey).length,
  }));
  // may this user submit despite every rule that is not met? (old Pending POs: super admin only)
  const canBypass = draft.canBypass;
  const allowed =
    !below ||
    ((!below.belowTonnage || Boolean(canBypass?.tonnage)) &&
      (below.belowMoq.length === 0 || Boolean(canBypass?.moq)) &&
      (oldPending.length === 0 || Boolean(canBypass?.oldPendingPo)));
  const [transport, setTransport] = useState<number>(UTradeTransport);
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-20 fw-bolder">Submit {draft.refKey} for approval</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="fs-14 text-gray-700">
          A Pending PO will be created for{" "}
          <span className="fw-bold">
            {draft.business?.name}
            {draftGroupLabel(draft) ? ` · ${draftGroupLabel(draft)}` : ""}
          </span>{" "}
          with{" "}
          {draft.lines.length} products. The draft is then closed and linked to the new PO.
        </p>
        <DraftCreditNote credit={draft.credit} />
        {below && (
          <div className="alert alert-warning fs-14">
            <div className="fw-bold">Below the supplier rules</div>
            {below.belowTonnage && <div>{below.missingTon} t below the {below.tonnageTon} t PO tonnage.</div>}
            {below.belowMoq.length > 0 && <div>{below.belowMoq.length} product(s) below MOQ.</div>}
            {oldPending.length > 0 && (
              <div>
                {oldPending.length} product(s) already on an old Pending PO:{" "}
                {oldPos.map((p) => `${p.refKey} (${p.ageDays} days, ${p.products} product${p.products === 1 ? "" : "s"})`).join(", ")}.
              </div>
            )}
            {allowed ? (
              <Form.Check
                className="mt-2"
                id="confirm-bypass"
                type="checkbox"
                label={
                  oldPending.length > 0
                    ? "As super admin I approve this draft despite the old Pending PO(s) (recorded on the timeline)"
                    : "I confirm submitting below the supplier tonnage / MOQ (recorded on the timeline)"
                }
                checked={confirmBypass}
                onChange={(e) => setConfirmBypass(e.target.checked)}
              />
            ) : (
              <div className="mt-2 fw-bold text-danger">
                {oldPending.length > 0 && !canBypass?.oldPendingPo
                  ? "Only a super admin can approve and submit this draft."
                  : "You are not allowed to submit below the supplier tonnage / MOQ. Ask a super admin."}
              </div>
            )}
          </div>
        )}
        <Form.Label className="fs-14 fw-600 required">Expected delivery date</Form.Label>
        <Form.Control type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)} />
        <Form.Label className="fs-14 fw-600 mt-4 required">Transport</Form.Label>
        <div className="d-flex gap-6">
          <Form.Check type="radio" id="t-utrade" label="UTrade Transport" checked={transport === UTradeTransport} onChange={() => setTransport(UTradeTransport)} />
          <Form.Check type="radio" id="t-self" label="Self Transport" checked={transport === SelfTransport} onChange={() => setTransport(SelfTransport)} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button className="uw-btn uw-btn-secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button
          className="uw-btn uw-btn-primary"
          disabled={!date || saving || !allowed || (Boolean(below) && !confirmBypass)}
          onClick={async () => {
            setSaving(true);
            const res = await callPatch(
              draft._id,
              "submit",
              { deliveryDate: new Date(`${date}T12:00:00`).toISOString(), shipmentType: transport, ...(below ? { confirmBypass } : {}) },
              warehouse
            );
            setSaving(false);
            if (res && res.status === "submitted") onSubmitted(res.submittedPo ?? { refKey: "" });
          }}
        >
          {saving ? <Spinner animation="border" size="sm" /> : "Submit as Pending PO"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
